/**
 * Trip CO₂ Calculator — Meet Jody conversion CTA.
 *
 * The primary funnel into Jody signup (originally into the waitlist;
 * flipped to direct account creation when the waitlist sunset,
 * Aug 2026). Sits below the perspective stat, near the bottom of the
 * result page. Composes a fixed copy block (eyebrow + headline + sub
 * + bullets) with the reusable `SignupCTAButton`.
 *
 * Origin/destination names are interpolated into the sub-line so the
 * pitch addresses *this trip* rather than reading like generic copy.
 *
 * Phase 6 of the Trip CO₂ Calculator (PRD §6.7).
 */

import React from 'react';
import SignupCTAButton from './SignupCTAButton';

const BULLETS: ReadonlyArray<{ icon: string; text: string }> = [
  { icon: '✨', text: 'Smart and personalized routes' },
  { icon: '💎', text: 'Hidden gems & must-see landmarks' },
  { icon: '🌿', text: 'Climate handled: calculate, reduce, compensate' },
];

export interface MeetJodyCTAProps {
  /**
   * Trip endpoint names used to personalise the sub-line copy.
   * Optional because the CTA is now also rendered alongside the
   * quota-gate banner, where the user may have hit the limit before
   * completing a trip in this session (e.g. deeplink refresh after
   * the quota reset). When either is missing, the sub-line falls
   * back to a generic version that reads naturally on its own.
   */
  originName?: string;
  destinationName?: string;
  /**
   * Which mount fired the click: the regular bottom-of-results CTA
   * ('travel_impact_calculator_main', default) or the copy rendered
   * under the quota-gate banner ('travel_impact_calculator_quota_gate').
   */
  source?: 'travel_impact_calculator_main' | 'travel_impact_calculator_quota_gate';
  /**
   * Forwarded to the embedded `SignupCTAButton` so the
   * `travel_impact_calculator_signup_click` Mixpanel event picks
   * up the trip context (origin, destination, mode, roundTrip,
   * co2eKg, distanceKm). The page builds it once from the submitted
   * trip and passes it down to every signup surface.
   */
  trackingProps?: Record<string, unknown>;
}

const MeetJodyCTA: React.FC<MeetJodyCTAProps> = ({
  originName,
  destinationName,
  source = 'travel_impact_calculator_main',
  trackingProps,
}) => (
  <section
    aria-label="Meet Jody"
    style={{
      marginTop: '2rem',
      padding: '1.5rem 1.25rem',
      background: 'linear-gradient(135deg, hsl(145, 71%, 23%), hsl(147, 72%, 37%))',
      color: '#fff',
      borderRadius: 14,
      boxShadow: '0 16px 32px rgba(4, 120, 87, 0.24)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: '#fff',
        background: 'rgba(255, 255, 255, 0.18)',
        border: '1.5px solid rgba(255, 255, 255, 0.25)',
        borderRadius: 999,
        marginBottom: '0.9rem',
      }}
    >
      ✦ Meet Jody
    </div>

    <h2
      style={{
        // Explicit white because CRA / browser h2 resets sometimes
        // override the parent's color: inherit — easier to pin here
        // than to fight cascade order from outside the file.
        color: '#fff',
        fontSize: 'clamp(1.5rem, 5vw, 1.75rem)',
        fontWeight: 800,
        lineHeight: 1.15,
        letterSpacing: -0.5,
        margin: '0 0 0.6rem',
      }}
    >
      Fewer flights, more friends, more fun.
    </h2>

    <p
      style={{
        margin: '0 0 1rem',
        fontSize: 15,
        opacity: 0.92,
        lineHeight: 1.5,
      }}
    >
      {originName && destinationName ? (
        <>
          Discover beautiful experiences between <b>{originName}</b> and{' '}
          <b>{destinationName}</b>:
        </>
      ) : (
        <>Discover beautiful experiences on your next sustainable journey:</>
      )}
    </p>

    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: '0 0 1.1rem',
        display: 'grid',
        gap: 6,
      }}
    >
      {BULLETS.map((b) => (
        <li
          key={b.text}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, fontWeight: 600 }}
        >
          <span style={{ flex: '0 0 auto', width: '1.4rem', textAlign: 'center' }}>{b.icon}</span>
          <span style={{ opacity: 0.96, lineHeight: 1.4 }}>{b.text}</span>
        </li>
      ))}
    </ul>

    <SignupCTAButton source={source} trackingProps={trackingProps} />

    <p
      style={{
        margin: '0.8rem 0 0',
        fontSize: 11,
        color: 'rgba(255, 255, 255, 0.85)',
        textAlign: 'center',
      }}
    >
      Free · No credit card needed
    </p>
  </section>
);

export default MeetJodyCTA;
